/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import BaseController from './base';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { computed, get } from '@ember/object';
import { setDiff, filterBy } from '@ember/object/computed';
import { later } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import {
  generateLeaverRequestParams,
  validateLeaver,
} from 'linced-app/utils/nz-moe/leaver';
import Resource from 'linced-app/enums/enrolment-resource';
import JurisdictionalStatus from 'linced-app/enums/jurisdictional-status';
import config from 'linced-app/config/environment';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';

export default class LeaversController extends BaseController {
  // Services
  @service
  store;

  _resultModel = 'enrolments/unenrolment';
  // uploadRouteName is refreshed when the upload dialog closes.
  uploadRouteName = 'nz-moe.bulk-upload.leavers';

  // sortedUploads provides a "most recent first" list of previous upload
  // attempts for records which are pending upload.
  @computed('model.uploads.@each.createTime')
  get sortedUploads() {
    return this.model.uploads
      .slice()
      .sort((a, b) => a.createTime - b.createTime)
      .reverse();
  }

  @computed('model.{enrolments.[],people.[]}', 'sortedUploads.[]')
  get allLeavers() {
    return this.model.enrolments.map((enrolment) => {
      let person = this.store.peekRecord('person', enrolment.personId);
      let validationErrors = validateLeaver(person, enrolment, this.schoolType);
      let previousUpload = this.sortedUploads.find(
        (item) => item.get('personEnrolment.id') === enrolment.id,
      );
      return {
        enrolment,
        person,
        validationErrors,
        isValid: validationErrors.length === 0,
        previousUpload,
      };
    });
  }

  @filterBy('allLeavers', 'isValid')
  validLeavers;

  @setDiff('allLeavers', 'validLeavers')
  invalidLeavers;

  @tracked
  validSelected = [];

  @computed('validSelected.length', 'validLeavers.length')
  get validAllSelected() {
    // If at least one is selected, and the same number are selected as are valid, they're all selected.
    return (
      this.validSelected.length &&
      this.validSelected.length === this.validLeavers.length
    );
  }

  @tracked
  invalidSelected = [];

  @tracked
  uploadComplete;

  @tracked
  uploadInProgress;

  @tracked
  uploadStatus;

  @tracked
  markUploadedDialogOpen;

  @tracked
  markUploadedInProgress;

  @tracked
  markUploadedSelected;

  @computed('invalidSelected.length', 'invalidLeavers.length')
  get invalidAllSelected() {
    // If at least one is selected, and the same number are selected as are invalid, they're all selected.
    return (
      this.invalidSelected.length &&
      this.invalidSelected.length === this.invalidLeavers.length
    );
  }

  // exportData transforms the upload results into the printable report
  // required by the Ministry.
  @computed('uploadResults.[]')
  get exportData() {
    if (isEmpty(this.uploadResults)) {
      return [];
    }

    return sortBy(
      this.uploadResults,
      'statusText',
      'personEnrolment.exitDate',
      'person.fullName',
    ).map((result) => {
      return [
        result.get('person.jurisdictionalId') || '',
        result.get('person.firstName') || '',
        result.get('person.lastName') || '',
        result.get('personEnrolment.exitDate') || '',
        result.get('statusText') || '',
        result.get('statusDescription') || '',
      ];
    });
  }

  performBulkUpload() {
    let params = this.validSelected.map((arrival) => {
      return generateLeaverRequestParams(
        arrival.person,
        arrival.enrolment,
        this.session,
      );
    });
    let status = this.store.createRecord('enrolments/status', {
      schoolId: this.session.school,
      requesterId: this.session.person,
      jurisdiction: Jurisdiction.NZ_MOE,
      resource: Resource.UNENROLMENTS,
      nzMoe: params,
    });
    this.uploadStatus = status;
    // Although we don't expect the whole process to finish quickly,
    // polling for status updates frequently allows us to show progress.
    status.save().then(() => later(this, 'updateUploadStatus', 1000));
  }

  processUploadResults(results) {
    let chain = results.reduce((previous, result) => {
      return previous.then(() => {
        switch (result.get('nzMoe.type')) {
          case 'J':
          case 'I':
            // We got a "successy" response.
            return this.markUploaded(result);

          case 'G':
            // NSN not found.
            return this.removeNSN(result);
        }
        return Promise.resolve();
      });
    }, Promise.resolve());
    chain.finally(() => {
      // Once we've processed any required updates,
      // mark the upload process as complete.
      this.uploadInProgress = false;
      this.uploadComplete = true;
    });
  }

  // markUploaded marks the jurisdictional enrolment status of the uploaded
  // enrolment record as "unenrolled", if this hasn't happened automatically.
  // NOTE: No toasts on this, as it would get spammy since it's a bulk process.
  markUploaded(result) {
    return result
      .get('personEnrolment')
      .reload()
      .then((enrolment) => {
        if (
          enrolment.jurisdictionalStatus ===
          JurisdictionalStatus.PENDING_UNENROLMENT
        ) {
          enrolment.set(
            'jurisdictionalStatus',
            JurisdictionalStatus.UNENROLLED,
          );
          return enrolment.save();
        }
        return Promise.resolve();
      });
  }

  performBulkMarkUploaded() {
    let url = `${config.APP.apiHost}/enrolments/${config.APP.apiPath}/unenrolments/:markUploaded`;
    let personEnrolmentIds = this.markUploadedSelected.map((item) =>
      get(item, 'enrolment.id'),
    );
    let schoolId = this.session.activeSchool;
    let data = {
      schoolId,
      personEnrolmentIds,
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        // Notify the user of a successful "bulk mark uploaded" process.
        this.paperToaster.show(
          'The selected leavers were marked as uploaded.',
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          },
        );
      })
      .catch(() => {
        // Notify the user of a failed "bulk mark uploaded" process.
        this.paperToaster.show(
          'Something went wrong while marking the selected leavers as uploaded.',
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      })
      .then(() => {
        // Reload the enrolments, to remove the ones we just flagged as uploaded.
        // Do this whether or not we got a "successy" response, in case the
        // process partially completed and then threw an error.
        return this.model.enrolments.update();
      })
      .finally(() => {
        // Reset "mark uploaded" properties.
        this.markUploadedDialogOpen = false;
        this.markUploadedSelected = [];
        this.markUploadedInProgress = false;
        // Reset selected lists (both, since we don't know which was used).
        this.validSelected = [];
        this.invalidSelected = [];
        // Update pending upload counts.
        this.students.updatePendingUploads();
      });
  }
}
